import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Switch } from "@/components/ui/switch";
import {
  MapPin,
  Plus,
  MoreHorizontal,
  Edit2,
  Trash2,
  Loader2,
  Building,
  Phone,
  Webhook,
  Clock
} from "lucide-react";
import { format, parseISO } from "date-fns";
import { getErrorMessage } from "@/lib/utils";

export default function Locations() {
  const { token, user } = useAuth();
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    address: "",
    phone: "",
    membership_duration_days: 365,
    webhook_url: "",
    webhook_api_token: "",
    webhook_uid: ""
  });

  const fetchLocations = async () => {
    try {
      const response = await axios.get(`${API}/locations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLocations(response.data);
    } catch (error) {
      console.error("Error fetching locations:", error);
      toast.error("Failed to load locations");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLocations();
  }, [token]);

  const handleOpenDialog = (location = null) => {
    if (location) {
      setSelectedLocation(location);
      setFormData({
        name: location.name,
        address: location.address || "",
        phone: location.phone || "",
        membership_duration_days: location.membership_duration_days || 365,
        webhook_url: location.webhook_url || "",
        webhook_api_token: location.webhook_api_token || "",
        webhook_uid: location.webhook_uid || ""
      });
    } else {
      setSelectedLocation(null);
      setFormData({
        name: "",
        address: "",
        phone: "",
        membership_duration_days: 365,
        webhook_url: "",
        webhook_api_token: "",
        webhook_uid: ""
      });
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name) {
      toast.error("Location name is required");
      return;
    }

    setSaving(true);
    try {
      if (selectedLocation) {
        await axios.put(`${API}/locations/${selectedLocation.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Location updated successfully");
      } else {
        await axios.post(`${API}/locations`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Location created successfully");
      }
      setDialogOpen(false);
      fetchLocations();
    } catch (error) {
      console.error("Error saving location:", error);
      toast.error(getErrorMessage(error, "Failed to save location"));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedLocation) return;

    setSaving(true);
    try {
      await axios.delete(`${API}/locations/${selectedLocation.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Location deleted successfully");
      setDeleteDialogOpen(false);
      fetchLocations();
    } catch (error) {
      console.error("Error deleting location:", error);
      toast.error("Failed to delete location");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (location) => {
    try {
      await axios.put(
        `${API}/locations/${location.id}`,
        { is_active: !location.is_active },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Location ${location.is_active ? "deactivated" : "activated"}`);
      fetchLocations();
    } catch (error) {
      console.error("Error toggling location:", error);
      toast.error("Failed to update location");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="locations-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Locations</h1>
          <p className="text-gray-500 mt-1">
            Manage your check-in locations
          </p>
        </div>
        {(user?.role === "super_admin" || user?.role === "main_office") && (
          <Button onClick={() => handleOpenDialog()} className="btn-press" data-testid="add-location-btn">
            <Plus className="mr-2 h-4 w-4" />
            Add Location
          </Button>
        )}
      </div>

      {/* Locations Grid */}
      {locations.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {locations.map((location) => (
            <Card 
              key={location.id} 
              className={`border-0 shadow-sm card-hover ${!location.is_active ? "opacity-60" : ""}`}
              data-testid={`location-card-${location.id}`}
            >
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-3 bg-purple-50 rounded-lg">
                      <MapPin className="h-5 w-5 text-purple-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{location.name}</h3>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        location.is_active 
                          ? "bg-green-100 text-green-800" 
                          : "bg-gray-100 text-gray-600"
                      }`}>
                        {location.is_active ? "Active" : "Inactive"}
                      </span>
                    </div>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" data-testid={`location-actions-${location.id}`}>
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => handleOpenDialog(location)}>
                        <Edit2 className="mr-2 h-4 w-4" />
                        Edit
                      </DropdownMenuItem>
                      {(user?.role === "super_admin" || user?.role === "main_office") && (
                        <DropdownMenuItem
                          className="text-red-600"
                          onClick={() => {
                            setSelectedLocation(location);
                            setDeleteDialogOpen(true);
                          }}
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          Delete
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                <div className="mt-4 space-y-2 text-sm">
                  {location.address && (
                    <div className="flex items-center gap-2 text-gray-500">
                      <Building className="h-4 w-4" />
                      {location.address}
                    </div>
                  )}
                  {location.phone && (
                    <div className="flex items-center gap-2 text-gray-500">
                      <Phone className="h-4 w-4" />
                      {location.phone}
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-gray-500">
                    <Clock className="h-4 w-4" />
                    Membership: {location.membership_duration_days || 365} days
                  </div>
                  <div className="flex items-center gap-2 text-gray-500">
                    <Webhook className="h-4 w-4" />
                    {location.webhook_url ? (
                      <span className="truncate max-w-[200px]">{location.webhook_url}</span>
                    ) : (
                      <span className="text-gray-400">No webhook configured</span>
                    )}
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t flex items-center justify-between">
                  <span className="text-xs text-gray-400">
                    Created {format(parseISO(location.created_at), "MMM d, yyyy")}
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500">Active</span>
                    <Switch
                      checked={location.is_active}
                      onCheckedChange={() => handleToggleActive(location)}
                      data-testid={`location-toggle-${location.id}`}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="border-0 shadow-sm">
          <CardContent className="p-12 text-center">
            <MapPin className="h-12 w-12 mx-auto mb-3 text-gray-300" />
            <p className="text-gray-500">No locations found</p>
            {(user?.role === "super_admin" || user?.role === "main_office") && (
              <Button variant="link" onClick={() => handleOpenDialog()} className="mt-2">
                Add your first location
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>{selectedLocation ? "Edit Location" : "Add Location"}</DialogTitle>
            <DialogDescription>
              {selectedLocation ? "Update location information" : "Add a new check-in location"}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">Location Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Main Office"
                data-testid="location-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="address">Address</Label>
              <Input
                id="address"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="123 Main Street, City, State"
                data-testid="location-address-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone">Phone</Label>
              <Input
                id="phone"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="555-123-4567"
                data-testid="location-phone-input"
              />
            </div>
            
            {/* Membership Duration Section */}
            <div className="pt-4 border-t">
              <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                <Clock className="h-4 w-4" />
                Membership Settings
              </h4>
              <div className="space-y-2">
                <Label htmlFor="membership_duration">Membership Duration (days)</Label>
                <Input
                  id="membership_duration"
                  type="number"
                  min="1"
                  value={formData.membership_duration_days}
                  onChange={(e) => setFormData({ ...formData, membership_duration_days: parseInt(e.target.value) || 365 })}
                  placeholder="365"
                  data-testid="location-membership-duration-input"
                />
                <p className="text-xs text-gray-500">
                  New members at this location will have their membership expire after this many days
                </p>
              </div>
            </div>

            {/* Webhook Configuration Section */}
            <div className="pt-4 border-t">
              <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                <Webhook className="h-4 w-4" />
                Webhook Configuration
              </h4>
              <p className="text-xs text-gray-500 mb-4">
                Check-in data will be sent as multipart/form-data with "uid" and "code" fields
              </p>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="webhook_url">Webhook URL</Label>
                  <Input
                    id="webhook_url"
                    value={formData.webhook_url}
                    onChange={(e) => setFormData({ ...formData, webhook_url: e.target.value })}
                    placeholder="https://your-api.com/webhook"
                    data-testid="location-webhook-url-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="webhook_api_token">API Token</Label>
                  <Input
                    id="webhook_api_token"
                    type="password"
                    value={formData.webhook_api_token}
                    onChange={(e) => setFormData({ ...formData, webhook_api_token: e.target.value })}
                    placeholder="Your API token (optional)"
                    data-testid="location-webhook-api-token-input"
                  />
                  <p className="text-xs text-gray-500">
                    Sent as "Api-Token" header with each webhook request
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="webhook_uid">UID</Label>
                  <Input
                    id="webhook_uid"
                    value={formData.webhook_uid}
                    onChange={(e) => setFormData({ ...formData, webhook_uid: e.target.value })}
                    placeholder="Unique identifier (optional)"
                    data-testid="location-webhook-uid-input"
                  />
                  <p className="text-xs text-gray-500">
                    Sent as "uid" form field along with the check-in "code"
                  </p>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-location-btn">
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              {selectedLocation ? "Save Changes" : "Add Location"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Location</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{selectedLocation?.name}"? 
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={saving} data-testid="confirm-delete-location-btn">
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
